'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Info } from 'lucide-react';
import { LinkButton } from '@/components/ui/Button/Button';
import { InfoModal } from '@/components/ui/InfoModal/InfoModal';
import { TONE_SHARING_POLICY_URL } from '@/utils/toneSharingPolicy';
import { toTonePathname } from '@/utils/helpers';
import { MODAL_COPY, accountStrikeText } from '@/utils/tone-policy/copy';
import type { OwnerNotice, OwnerNotices } from '@/utils/tone-policy/notice';

const SEEN_KEY = 'tonePolicyNoticesSeen';

function seenVerdictIds(): number[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

interface Props {
  notices: OwnerNotices;
  /** Called once nothing is left to show, so the next modal can open. */
  onDone?: () => void;
}

const TonePolicyNoticeModal = ({ notices, onDone }: Props) => {
  const [unseen, setUnseen] = useState<OwnerNotice[]>([]);

  useEffect(() => {
    const seen = new Set(seenVerdictIds());
    const next = notices.tones.filter((n) => !seen.has(n.verdictId));
    setUnseen(next);
    if (next.length === 0) onDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notices]);

  const dismiss = () => {
    const ids = Array.from(new Set([...seenVerdictIds(), ...unseen.map((n) => n.verdictId)]));
    localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
    setUnseen([]);
    onDone?.();
  };

  return (
    <InfoModal
      open={unseen.length > 0}
      onClose={dismiss}
      icon={<Info size={24} className="text-white" />}
      title={MODAL_COPY.title(unseen.length)}
      className="h-[520px] max-h-[none]"
      message={
        <div className="flex flex-col gap-4 text-zinc-400">
          <div className="flex flex-col gap-3">
            <span>{MODAL_COPY.summary(unseen.length)}</span>
            <span>{MODAL_COPY.howItWorks}</span>
          </div>
          {notices.activeStrikes > 0 && (
            <span className="text-red-400 font-semibold">
              {accountStrikeText(notices.activeStrikes)}
            </span>
          )}
          {unseen.map((tone) => (
            <div key={tone.verdictId} className="flex flex-col gap-2">
              <Link
                href={`/tones/${toTonePathname({ id: tone.toneId, title: tone.toneTitle })}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-100 font-semibold underline hover:text-white"
              >
                {tone.toneTitle}
              </Link>
              {tone.reasons.map((reason) => (
                <span key={reason}>{reason}</span>
              ))}
              <span className={tone.strike ? 'text-red-400 font-semibold' : undefined}>
                {MODAL_COPY.outcome(tone.strike, tone.action)}
              </span>
            </div>
          ))}
        </div>
      }
      action={
        <LinkButton href={TONE_SHARING_POLICY_URL} isPrimary className="w-full">
          Read the Guidelines
        </LinkButton>
      }
    />
  );
};

export default TonePolicyNoticeModal;
